import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { OBLIGES, fmt, euros } from "@/lib/cee";

const Vase = ({ name, v, objectif, onObjectif }) => {
  const pct = objectif > 0 ? Math.min(100, (v.mwh / objectif) * 100) : 0;
  const slug = name.toLowerCase();
  return (
    <div data-testid={`vase-${slug}`} className="flex flex-col items-center rounded-2xl border border-[var(--line)] bg-[var(--inset)] p-5">
      <span className="font-display mb-3 text-sm font-bold tracking-wide text-[var(--ink)]">{name}</span>
      <div className="relative h-44 w-28 overflow-hidden rounded-b-[2.5rem] rounded-t-xl border-2 border-sky-500/40 bg-[var(--bg)]">
        <div
          data-testid={`vase-fill-${slug}`}
          className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-sky-600 to-sky-400 transition-[height] duration-1000 ease-out"
          style={{ height: `${pct}%` }}
        />
        <span className="absolute inset-0 flex items-center justify-center font-mono text-lg font-bold text-[var(--ink)] mix-blend-difference">
          {objectif > 0 ? `${fmt(pct)} %` : "—"}
        </span>
      </div>
      <p data-testid={`vase-volume-${slug}`} className="mt-3 text-center text-xs text-[var(--dim)]">
        <b className="text-[var(--ink)]">{fmt(v.mwh, 1)}</b> MWh cumac · {v.dossiers} dossier{v.dossiers > 1 ? "s" : ""}
      </p>
      <p className="text-[11px] text-[var(--mute)]">dont précarité {fmt(v.mwhPrecarite, 1)} MWh</p>
      <dl data-testid={`vase-recap-${slug}`} className="mt-3 w-full space-y-1 rounded-xl border border-[var(--line)] p-3 text-xs">
        {[["Prime", v.prime, "prime"], ["Commission", v.commission, "commission"], ["Part Genis", v.genis, "genis"]].map(([l, val, k]) => (
          <div key={k} className="flex justify-between gap-2">
            <dt className="text-[var(--mute)]">{l}</dt>
            <dd data-testid={`vase-${k}-${slug}`} className="font-mono font-bold text-[var(--ink)]">{euros(Math.round(val || 0))}</dd>
          </div>
        ))}
      </dl>
      <label className="mt-3 w-full text-[10px] font-mono uppercase tracking-widest text-[var(--dim)]">Objectif (MWh cumac)</label>
      <input data-testid={`vase-objectif-${slug}`} type="number" min="0" step="100" className="field-input mt-1 font-mono"
        value={objectif} onChange={(e) => onObjectif(e.target.value)} />
    </div>
  );
};

export const ObligeVases = ({ parOblige, onSaved }) => {
  const [obj, setObj] = useState(null);
  useEffect(() => {
    api.get("/admin/obliges-objectifs").then(({ data }) => setObj(data)).catch(async (e) => toast.error(await apiErrorMessage(e)));
  }, []);
  const save = async () => {
    try {
      const { data } = await api.put("/admin/obliges-objectifs", Object.fromEntries(OBLIGES.map((o) => [o, parseFloat(obj[o]) || 0])));
      setObj(data);
      toast.success("Objectifs des obligés enregistrés.");
      onSaved?.();
    } catch (e) {
      toast.error(await apiErrorMessage(e));
    }
  };
  if (!obj || !parOblige) return null;
  return (
    <div data-testid="admin-oblige-vases" className="card-surface p-6">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-[var(--ink)]">Volume CEE par obligé</h2>
          <p className="text-xs text-[var(--mute)]">Chaque vase se remplit avec le volume des dossiers attribués à l'obligé, par rapport à son objectif.</p>
        </div>
        <button data-testid="vase-objectifs-save-btn" onClick={save} className="btn-primary px-4 py-2 text-sm">
          <Save size={14} /> Enregistrer les objectifs
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {OBLIGES.map((o) => (
          <Vase key={o} name={o} v={parOblige[o] || { mwh: 0, dossiers: 0, mwhPrecarite: 0 }} objectif={obj[o] ?? 0}
            onObjectif={(val) => setObj((p) => ({ ...p, [o]: val }))} />
        ))}
      </div>
    </div>
  );
};
